import { useMemo } from "react"
import { ImageProcessingState } from "@nova/constants"
import { requiresCsvTelemetry } from "./registry"

const fileNameOf = (u: unknown): string | undefined => {
    if (typeof u !== "object" || u === null) return undefined
    const fileName = (u as Record<string, unknown>).fileName
    return typeof fileName === "string" ? fileName : undefined
}

const stateOf = (u: unknown): ImageProcessingState | undefined => {
    if (typeof u !== "object" || u === null || !("state" in u)) return undefined
    return (u as { state: ImageProcessingState }).state
}

/**
 * Upload-gating derived from MMA telemetry, shared by the InfraredImage and
 * Orthomosaic preview panels. Files that need a CSV (.png, .owi) cannot upload
 * until telemetry is attached and every one has a matching row.
 *
 * - `csvPending`         — at least one CSV-dependent file is still waiting for
 *   telemetry that hasn't been attached to this flight yet.
 * - `hasInvalidTelemetry` — at least one CSV-dependent file found no matching row.
 */
export const useCsvTelemetryGating = (params: {
    uploads: unknown[]
    flightId: string
    csvFlights: Record<string, true>
}) => {
    const { uploads, flightId, csvFlights } = params

    const hasCsvUploads = useMemo(
        () => uploads.some((u) => requiresCsvTelemetry(fileNameOf(u) ?? "")),
        [uploads]
    )

    const csvPending = useMemo(
        () =>
            hasCsvUploads &&
            !csvFlights[flightId] &&
            uploads.some((u) => {
                if (!requiresCsvTelemetry(fileNameOf(u) ?? "")) return false
                const state = stateOf(u)
                return (
                    state === ImageProcessingState.Pending ||
                    state === ImageProcessingState.Processing
                )
            }),
        [hasCsvUploads, uploads, csvFlights, flightId]
    )

    const hasInvalidTelemetry = useMemo(
        () =>
            hasCsvUploads &&
            uploads.some((u) => {
                if (typeof u !== "object" || u === null) return false
                const err = (u as Record<string, unknown>).error
                return (
                    typeof err === "object" &&
                    err !== null &&
                    (err as { name?: string }).name === "InvalidTelemetry"
                )
            }),
        [hasCsvUploads, uploads]
    )

    return { csvPending, hasInvalidTelemetry }
}
